import { AbsenceType, Exchange } from '@pdcplan/shared/api/TeamPlan';
import { ExchangeTypeEnum } from '@pdcplan/shared/enums';

import { ListViewAction } from '../ListView/ListViewActions';
import { Reducer } from 'redux';
import { ShiftViewAction } from './ShiftViewActions';
import { ShiftDraft } from '../ListView/CreateShift/EditedShift';
import { ActiveOfferView } from './ShiftViewEnums';
import { LoginAction, LoginEvents } from '../Login/LoginAction';

export type ShiftViewReducer = {
  loading: boolean;
  absenceTypes: AbsenceType[];
  loadingSwap: boolean;
  swapDetails: Exchange | null;
  isSwapViewOpen: boolean;
  setOfferViewOpen: boolean;
  offerViewActiveView: ActiveOfferView;
  pendingAcceptRequest: boolean;
  pendingRejectRequest: boolean;
  acceptRequestEnabled: boolean;
  selectedExchangeId: number | null;
  shiftDraft: ShiftDraft | null;
  showConfirmDeleteShift: boolean;
  deleteShiftLoading: boolean;
  showConfirmEditShift: boolean;
  editShiftLoading: boolean;
};

const initialState: ShiftViewReducer = {
  loading: false,
  absenceTypes: [],
  loadingSwap: false,
  swapDetails: null,
  isSwapViewOpen: false,
  setOfferViewOpen: false,
  acceptRequestEnabled: false,
  pendingAcceptRequest: false,
  pendingRejectRequest: false,
  selectedExchangeId: null,
  shiftDraft: null,
  showConfirmDeleteShift: false,
  deleteShiftLoading: false,
  showConfirmEditShift: false,
  editShiftLoading: false,
  offerViewActiveView: ActiveOfferView.None,
};

const shiftViewReducer: Reducer<ShiftViewReducer> = (
  state: ShiftViewReducer = initialState,
  action: ShiftViewAction | ListViewAction | LoginAction
): ShiftViewReducer => {
  switch (action.type) {
    case LoginEvents.LOGOUT:
      return initialState;
    case 'GETABSENCETYPES_REQUEST':
      return {
        ...state,
        loading: true,
      };
    case 'GETABSENCETYPES_SUCCESS':
      return {
        ...state,
        loading: false,
        absenceTypes: action.getAbsenceTypesResponse,
      };
    case 'GETABSENCETYPES_FAILURE':
      return {
        ...state,
        loading: false,
      };
    case 'REGISTERABSENCE_REQUEST':
      return {
        ...state,
        loading: true,
      };
    case 'REGISTERABSENCE_SUCCESS':
      return {
        ...state,
        loading: false,
      };
    case 'REGISTERABSENCE_FAILURE':
      return {
        ...state,
        loading: false,
      };
    case 'MARKASHANDLED_REQUEST':
      return {
        ...state,
        loading: true,
      };
    case 'MARKASHANDLED_SUCCESS':
      return {
        ...state,
        loading: false,
      };
    case 'MARKASHANDLED_FAILURE':
      return {
        ...state,
        loading: false,
      };
    case 'OFFER_ON_SHIFT_ECHANGE_REQUEST':
      return {
        ...state,
        loading: true,
      };
    case 'OFFER_ON_SHIFT_ECHANGE_SUCCESS':
      return {
        ...state,
        loading: false,
        setOfferViewOpen: false,
      };
    case 'OFFER_ON_SHIFT_ECHANGE_FAILURE':
      return {
        ...state,
        loading: false,
      };
    case 'CANCEL_OFFER_ON_SHIFT_ECHANGE_REQUEST':
      return {
        ...state,
        loading: true,
      };
    case 'CANCEL_OFFER_ON_SHIFT_ECHANGE_SUCCESS':
      return {
        ...state,
        loading: false,
      };
    case 'CANCEL_OFFER_ON_SHIFT_ECHANGE_FAILURE':
      return {
        ...state,
        loading: false,
      };
    case 'ASSIGNSHIFT_REQUEST':
      return {
        ...state,
        loading: true,
      };
    case 'ASSIGNSHIFT_SUCCESS':
      return {
        ...state,
        loading: false,
      };
    case 'ASSIGNSHIFT_FAILURE':
      return {
        ...state,
        loading: false,
      };

    case 'GETEXCHANGE_REQUEST':
      return {
        ...state,
        loadingSwap: true,
      };

    case 'GETEXCHANGE_SUCCESS':
      return {
        ...state,
        swapDetails: action.exchange,
        acceptRequestEnabled:
          action.exchange.requestInfo.type === ExchangeTypeEnum.swapRequest ||
          state.selectedExchangeId !== null,
        loadingSwap: false,
      };

    case 'SET_SWAP_VIEW_OPEN':
      return {
        ...state,
        isSwapViewOpen: action.open,
      };

    case 'SET_OFFER_VIEW_OPEN':
      return {
        ...state,
        setOfferViewOpen: action.open,
      };
    case 'SET_ACTIVE_OFFER_VIEW':
      return {
        ...state,
        offerViewActiveView: action.activeOfferView,
      };
    case 'EXCHANGE_ACCEPT_REQUEST':
      return {
        ...state,
        pendingAcceptRequest: true,
      };

    case 'EXCHANGE_REJECT_REQUEST':
      return {
        ...state,
        pendingRejectRequest: true,
      };

    case 'EXCHANGE_ACCEPT_SUCCESS':
    case 'EXCHANGE_REJECT_SUCCESS':
      return {
        ...state,
        pendingAcceptRequest: false,
        pendingRejectRequest: false,
        isSwapViewOpen: false,
        setOfferViewOpen: false,
        swapDetails: null,
      };

    case 'EXCHANGE_ACCEPT_FAILURE':
    case 'EXCHANGE_REJECT_FAILURE':
      return {
        ...state,
        pendingAcceptRequest: false,
        pendingRejectRequest: false,
      };

    case 'EXCHANGE_SELECT_EXCHANGE_ID':
      return {
        ...state,
        selectedExchangeId: action.exchangeId ?? null,
        acceptRequestEnabled: action.exchangeId !== undefined,
      };

    case 'SET_SHIFT_DRAFT':
      return {
        ...state,
        shiftDraft: action.data,
      };

    case 'SET_CONFIRM_DELETE_MODAL':
      return {
        ...state,
        showConfirmDeleteShift: action.shown,
      };
    case 'REMOVE_SHIFT_REQUEST':
      return {
        ...state,
        deleteShiftLoading: true,
      };
    case 'REMOVE_SHIFT_FAILURE':
    case 'REMOVE_SHIFT_SUCCESS':
      return {
        ...state,
        deleteShiftLoading: false,
        showConfirmDeleteShift: false,
      };
    case 'SET_CONFIRM_EDIT_MODAL':
      return {
        ...state,
        showConfirmEditShift: action.shown,
      };
    case 'EDIT_SHIFT_REQUEST':
      return {
        ...state,
        editShiftLoading: true,
      };
    case 'EDIT_SHIFT_FAILURE':
    case 'EDIT_SHIFT_SUCCESS':
      return {
        ...state,
        editShiftLoading: false,
      };
  }

  return state;
};

export default shiftViewReducer;
